import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Sparkles, Check, Trash2, RefreshCw, ShieldCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtNum, fmtMoney, fmtDate } from "@/lib/format";
import { Kpi, Panel, Badge2, PageHead } from "@/components/Bits";
import { AiLocked, useTrial } from "@/components/TrialGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const confTone = (c) => (c >= 90 ? "good" : c >= 70 ? "primary" : c >= 50 ? "warn" : "bad");

export default function BOQParser() {
  const { pid } = useParams();
  const { status } = useTrial();
  const aiLocked = status && !status.ai_unlocked;
  const fileRef = useRef(null);
  const [engine, setEngine] = useState("ai");
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);
  const [history, setHistory] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [group, setGroup] = useState({ group_code: "BOQ-01", group_name: "Imported BOQ Scope", chain_sequentially: true });

  const loadHistory = () => api.get("/boq/sessions", { params: { project_id: pid } }).then(({ data }) => setHistory(data));
  useEffect(() => { if (!aiLocked) loadHistory(); }, [pid, aiLocked]);

  if (aiLocked) return <div data-testid="boq-page"><AiLocked feature="The AI BOQ Parser" /></div>;

  const upload = async (file) => {
    if (!file) return;
    const body = new FormData();
    body.append("project_id", pid);
    body.append("engine", engine);
    body.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/boq/parse", body, { headers: { "Content-Type": "multipart/form-data" } });
      setSession(data);
      toast.success(`${data.row_count} BOQ lines extracted · ${data.avg_confidence}% average confidence`);
      if (data.warning) toast.warning(data.warning);
      loadHistory();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const patch = async (rowId, patchBody) => {
    try {
      const { data } = await api.put(`/boq/sessions/${session.id}/rows/${rowId}`, patchBody);
      setSession((s) => ({ ...s, rows: s.rows.map((r) => (r.id === rowId ? data : r)) }));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const confirmImport = async () => {
    try {
      const { data } = await api.post(`/boq/sessions/${session.id}/confirm`, group);
      toast.success(`${data.imported} activities created in the WBS · ${fmtMoney(data.value)} scope value`);
      setConfirmOpen(false);
      setSession(null);
      loadHistory();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openSession = async (sid) => {
    const { data } = await api.get(`/boq/sessions/${sid}`);
    setSession(data);
  };

  const rows = session?.rows || [];
  const included = rows.filter((r) => r.include);
  const lowConf = rows.filter((r) => r.confidence < 70).length;

  return (
    <div data-testid="boq-page">
      <PageHead title="AI BOQ Parser" subtitle="Drop any client PDF or Excel BOQ. The NLP engine maps it to activity, unit, quantity and rate — you only correct what it flags.">
        <Select value={engine} onValueChange={setEngine}>
          <SelectTrigger className="w-[190px]" data-testid="boq-engine-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ai">AI + NLP extraction</SelectItem>
            <SelectItem value="heuristic">Rule-based columns only</SelectItem>
          </SelectContent>
        </Select>
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.xlsx,.xlsm,.csv"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid="boq-file-input"
        />
        <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="boq-upload-btn">
          <Upload className="w-4 h-4 mr-1.5" /> {busy ? "Parsing…" : "Upload BOQ"}
        </Button>
      </PageHead>

      {!session ? (
        <div
          className="border border-dashed border-border p-10 text-center bg-card"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files?.[0]); }}
          data-testid="boq-dropzone"
        >
          <Sparkles className="w-7 h-7 text-[#F97316] mx-auto" />
          <h3 className="font-semibold mt-3">Drop a BOQ file to begin</h3>
          <p className="text-[13px] text-muted-foreground mt-1.5 max-w-xl mx-auto leading-relaxed">
            Supports client PDFs with tables, .xlsx / .xlsm workbooks and .csv exports up to 30 MB. Units are normalised
            automatically (Cum → Cu.M, Mtr → RMT, Ton → MT) and every row carries a confidence score you can override
            before it reaches the WBS.
          </p>
          <Button className="mt-5" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="boq-dropzone-btn">
            <Upload className="w-4 h-4 mr-1.5" /> Select file
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-3">
            <Kpi label="Lines extracted" value={session.row_count} testId="kpi-boq-rows" />
            <Kpi label="Average confidence" value={`${session.avg_confidence}%`} tone={confTone(session.avg_confidence) === "good" ? "good" : "warn"} testId="kpi-boq-confidence" />
            <Kpi label="Needs review (<70%)" value={lowConf} tone={lowConf ? "warn" : "good"} testId="kpi-boq-review" />
            <Kpi label="Scope value" value={fmtMoney(included.reduce((s, r) => s + r.amount, 0))} tone="primary" />
            <Kpi label="Engine used" value={session.engine_used === "ai" ? "AI + NLP" : "Rule-based"} sub={`${session.filename} · ${session.size_kb} KB`} />
          </div>

          <Panel
            title="Parsed rows — edit any cell to lock it at 100% confidence"
            testId="boq-rows-panel"
            action={
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => setSession(null)} data-testid="boq-discard-btn">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> New file
                </Button>
                <Button size="sm" onClick={() => setConfirmOpen(true)} disabled={!included.length || session.status === "confirmed"} data-testid="boq-confirm-open-btn">
                  <Check className="w-3.5 h-3.5 mr-1" /> Import {included.length} rows to WBS
                </Button>
              </div>
            }
          >
            <div className="overflow-x-auto max-h-[60vh]">
              <table className="w-full dense-table">
                <thead className="sticky-head">
                  <tr>
                    <th>Use</th><th>Code</th><th className="min-w-[260px]">Activity description</th><th>Unit</th>
                    <th className="text-right">Qty</th><th className="text-right">Rate</th><th className="text-right">Amount</th>
                    <th>Discipline</th><th>Confidence</th><th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className={r.confidence < 70 ? "bg-amber-500/5" : ""} data-testid={`boq-row-${r.id}`}>
                      <td>
                        <Checkbox checked={r.include} onCheckedChange={(v) => patch(r.id, { include: !!v })} data-testid={`boq-include-${r.id}`} />
                      </td>
                      <td>
                        <Input className="h-6 w-20 text-[12px] px-1 num" defaultValue={r.code}
                          onBlur={(e) => e.target.value !== r.code && patch(r.id, { code: e.target.value })}
                          data-testid={`boq-code-${r.id}`} />
                      </td>
                      <td>
                        <Input className="h-6 text-[12px] px-1" defaultValue={r.name}
                          onBlur={(e) => e.target.value !== r.name && patch(r.id, { name: e.target.value })}
                          data-testid={`boq-name-${r.id}`} />
                      </td>
                      <td>
                        <Input className="h-6 w-20 text-[12px] px-1" defaultValue={r.unit}
                          onBlur={(e) => e.target.value !== r.unit && patch(r.id, { unit: e.target.value })}
                          data-testid={`boq-unit-${r.id}`} />
                      </td>
                      <td>
                        <Input className="h-6 w-24 text-right num text-[12px] px-1" defaultValue={r.qty}
                          onBlur={(e) => Number(e.target.value) !== r.qty && patch(r.id, { qty: Number(e.target.value) })}
                          data-testid={`boq-qty-${r.id}`} />
                      </td>
                      <td>
                        <Input className="h-6 w-24 text-right num text-[12px] px-1" defaultValue={r.rate}
                          onBlur={(e) => Number(e.target.value) !== r.rate && patch(r.id, { rate: Number(e.target.value) })}
                          data-testid={`boq-rate-${r.id}`} />
                      </td>
                      <td className="num text-right">{fmtMoney(r.amount)}</td>
                      <td className="text-muted-foreground">{r.discipline || "—"}</td>
                      <td><Badge2 tone={confTone(r.confidence)} testId={`boq-conf-${r.id}`}>{r.confidence}%</Badge2></td>
                      <td className="text-[11px] text-muted-foreground max-w-[200px] truncate" title={r.source}>{r.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}

      <Panel title="Previous parse sessions" className="mt-3" testId="boq-history-panel">
        <div className="overflow-x-auto">
          <table className="w-full dense-table">
            <thead><tr><th>Date</th><th>File</th><th>Engine</th><th className="text-right">Rows</th><th className="text-right">Confidence</th><th className="text-right">Value</th><th>Status</th><th /></tr></thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} data-testid={`boq-session-${h.id}`}>
                  <td className="num">{fmtDate(h.created_at)}</td>
                  <td className="max-w-[220px] truncate">{h.filename}</td>
                  <td className="text-muted-foreground">{h.engine_used === "ai" ? "AI + NLP" : "Rule-based"}</td>
                  <td className="num text-right">{h.row_count}</td>
                  <td className="num text-right">{h.avg_confidence}%</td>
                  <td className="num text-right">{fmtMoney(h.total_value)}</td>
                  <td>
                    <Badge2 tone={h.status === "confirmed" ? "good" : "warn"}>
                      {h.status === "confirmed" ? `imported ${h.imported_activities || 0}` : "draft"}
                    </Badge2>
                  </td>
                  <td>
                    <div className="flex gap-1.5">
                      <button onClick={() => openSession(h.id)} className="text-muted-foreground hover:text-primary" title="Open" data-testid={`boq-open-${h.id}`}>
                        <FileSpreadsheet className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={async () => { await api.delete(`/boq/sessions/${h.id}`); loadHistory(); }} className="text-muted-foreground hover:text-red-500" data-testid={`boq-delete-${h.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {history.length === 0 ? <tr><td colSpan={8} className="text-muted-foreground">No BOQ files parsed yet.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </Panel>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Import to WBS</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="border border-border bg-muted/40 p-3 text-[13px] flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-[#F97316] mt-0.5 shrink-0" />
              <span>
                {included.length} rows worth {fmtMoney(included.reduce((s, r) => s + r.amount, 0))} will be created as
                Level-3 activities under a new WBS group. Dates are spread across the project window and can be edited
                afterwards on the WBS page.
              </span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs uppercase">Group code</Label>
                <Input value={group.group_code} onChange={(e) => setGroup({ ...group, group_code: e.target.value })} data-testid="boq-group-code-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase">Group name</Label>
                <Input value={group.group_name} onChange={(e) => setGroup({ ...group, group_name: e.target.value })} data-testid="boq-group-name-input" /></div>
            </div>
            <label className="flex items-center gap-2 text-[13px]">
              <Checkbox checked={group.chain_sequentially} onCheckedChange={(v) => setGroup({ ...group, chain_sequentially: !!v })} data-testid="boq-chain-checkbox" />
              Chain activities as finish-to-start predecessors (feeds the CPM engine)
            </label>
          </div>
          <DialogFooter><Button onClick={confirmImport} data-testid="boq-confirm-btn">Create activities</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
